/* ================================================================
   Context Menu - Right-click menu for selected objects.
   Supports single/multi selection parameter editing.
   Light Tailwind theme.
   ================================================================ */

import { useState, useEffect, useMemo, useLayoutEffect, useRef } from 'react';
import { Trash2, X, Wand2, ArrowUp, ArrowDown, Link2, Unlink2 } from 'lucide-react';
import { useEditorStore } from '../state/editorStore';
import type { SideId } from '../core/model/types';
import { getSideLabel } from '../core/renderTree';
import { STRUCTURAL_PRESETS } from '../core/presets';

// --------------- Add layer style picker ---------------

function AddLayerStylePicker({ onSelect }: { onSelect: (style: 0 | 1 | 3 | 4) => void }) {
  const styles: Array<{ value: 0 | 1 | 3 | 4; label: string }> = [
    { value: 0, label: '板条' },
    { value: 1, label: '整板' },
    { value: 3, label: '田字' },
    { value: 4, label: '木方' },
  ];
  return (
    <div className="grid grid-cols-4 gap-1 border-t border-birch-200 px-3 py-2">
      {styles.map(s => (
        <button
          key={s.value}
          onClick={() => onSelect(s.value)}
          className="rounded-md border border-birch-200 px-2 py-1.5 text-center text-[11px] font-medium text-walnut-700 hover:border-wood-500 hover:bg-wood-100"
        >
          {s.label}
        </button>
      ))}
    </div>
  );
}

// --------------- Main ContextMenu ----------------

export default function ContextMenu() {
  const contextMenu = useEditorStore(s => s.contextMenu);
  const hideContextMenu = useEditorStore(s => s.hideContextMenu);
  const deleteSelected = useEditorStore(s => s.deleteSelected);
  const addLayer = useEditorStore(s => s.addLayer);
  const sideLinkageMode = useEditorStore(s => s.project.sideLinkageMode ?? 'linked');
  const setSideLinkageMode = useEditorStore(s => s.setSideLinkageMode);
  const applyPreset = useEditorStore(s => s.applyPreset);
  const renderNodes = useEditorStore(s => s.renderNodes);
  const selectedMeshIds = useEditorStore(s => s.selectedMeshIds);
  const focusedSideId = useEditorStore(s => s.focusedSideId);
  const selCount = useEditorStore(s => Object.keys(s.selectedMeshIds).length);

  const [showPresets, setShowPresets] = useState(false);
  const [addLayerDir, setAddLayerDir] = useState<'up' | 'down' | null>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [menuPosition, setMenuPosition] = useState({ left: contextMenu.x, top: contextMenu.y });

  const targetSideIds = useMemo(() => {
    const ids = new Set<SideId>();
    for (const node of renderNodes) {
      if (selectedMeshIds[node.meshId]) ids.add(node.sideId);
    }
    if (ids.size > 0) return [...ids];
    if (focusedSideId) return [focusedSideId];
    return undefined;
  }, [focusedSideId, renderNodes, selectedMeshIds]);

  const presetTargetText = useMemo(() => {
    if (targetSideIds && targetSideIds.length > 0) {
      return `目标：${targetSideIds.map(getSideLabel).join('、')}`;
    }
    return '目标：所有启用面';
  }, [targetSideIds]);

  const selectedLayerTargets = useMemo(() => {
    const map = new Map<string, { sideId: SideId; layerIndex: number }>();
    for (const node of renderNodes) {
      if (!selectedMeshIds[node.meshId]) continue;
      const key = `${node.sideId}-${node.layerIndex}`;
      if (!map.has(key)) {
        map.set(key, { sideId: node.sideId, layerIndex: node.layerIndex });
      }
    }
    return [...map.values()];
  }, [renderNodes, selectedMeshIds]);

  const singleLayerTarget = selectedLayerTargets.length === 1 ? selectedLayerTargets[0] : null;

  useEffect(() => {
    setShowPresets(false);
    setAddLayerDir(null);
  }, [contextMenu.visible]);

  useLayoutEffect(() => {
    if (!contextMenu.visible) return;

    const overlay = overlayRef.current;
    const menu = menuRef.current;
    if (!overlay || !menu) return;

    const padding = 8;
    const maxLeft = Math.max(padding, overlay.clientWidth - menu.offsetWidth - padding);
    const maxTop = Math.max(padding, overlay.clientHeight - menu.offsetHeight - padding);

    setMenuPosition({
      left: Math.min(Math.max(contextMenu.x, padding), maxLeft),
      top: Math.min(Math.max(contextMenu.y, padding), maxTop),
    });
  }, [contextMenu.visible, contextMenu.x, contextMenu.y, showPresets, addLayerDir, selCount]);

  if (!contextMenu.visible) return null;

  return (
    <div
      ref={overlayRef}
      className="absolute inset-0 z-30"
      onClick={hideContextMenu}
      onContextMenu={e => e.preventDefault()}
    >
      <div
        ref={menuRef}
        className="absolute max-h-[calc(100%-16px)] w-[264px] overflow-y-auto rounded-md border border-birch-200 bg-white text-[12px] text-walnut-700 shadow-pop"
        style={{ left: menuPosition.left, top: menuPosition.top }}
        onClick={e => e.stopPropagation()}
      >
        {singleLayerTarget ? (
          <>
            <button
              onClick={() => setAddLayerDir(addLayerDir === 'up' ? null : 'up')}
              className="flex w-full items-center gap-2 border-t border-birch-200 px-3 py-2 text-left hover:bg-pine-50"
            >
              <ArrowUp className="h-4 w-4 text-wood-500" />
              <span>向上添加层</span>
            </button>
            {addLayerDir === 'up' && (
              <AddLayerStylePicker
                onSelect={(style) => {
                  addLayer(singleLayerTarget.sideId, singleLayerTarget.layerIndex + 1, style, 0);
                  hideContextMenu();
                }}
              />
            )}
            <button
              onClick={() => setAddLayerDir(addLayerDir === 'down' ? null : 'down')}
              className="flex w-full items-center gap-2 border-t border-birch-200 px-3 py-2 text-left hover:bg-pine-50"
            >
              <ArrowDown className="h-4 w-4 text-wood-500" />
              <span>向下添加层</span>
            </button>
            {addLayerDir === 'down' && (
              <AddLayerStylePicker
                onSelect={(style) => {
                  addLayer(singleLayerTarget.sideId, singleLayerTarget.layerIndex, style, 0);
                  hideContextMenu();
                }}
              />
            )}
          </>
        ) : null}
        <button
          onClick={() => setSideLinkageMode(sideLinkageMode === 'linked' ? 'independent' : 'linked')}
          className="flex w-full items-center gap-2 border-t border-birch-200 px-3 py-2 text-left hover:bg-pine-50"
          title="切换前后板与左右端板是否跟随相邻面的厚度联动"
        >
          {sideLinkageMode === 'linked' ? (
            <Link2 className="h-4 w-4 text-wood-500" />
          ) : (
            <Unlink2 className="h-4 w-4 text-wood-500" />
          )}
          <span>{sideLinkageMode === 'linked' ? '联动已开启' : '联动已关闭'}</span>
        </button>
        <button
          onClick={() => setShowPresets(v => !v)}
          className="flex w-full items-center gap-2 border-t border-birch-200 px-3 py-2 text-left hover:bg-pine-50"
        >
          <Wand2 className="h-4 w-4 text-wood-500" />
          <span>一键应用</span>
        </button>
        {showPresets ? (
          <div className="border-t border-birch-200 px-3 py-2">
            <div className="mb-2 text-[10px] text-oak-300">{presetTargetText}</div>
            <div className="space-y-1">
              {STRUCTURAL_PRESETS.map((preset) => (
                <button
                  key={preset.id}
                  onClick={() => {
                    applyPreset(preset, targetSideIds);
                    hideContextMenu();
                  }}
                  className="w-full rounded-md px-2.5 py-2 text-left hover:bg-wood-100"
                >
                  <div className="text-[12px] font-medium text-walnut-900">{preset.name}</div>
                  <div className="mt-0.5 text-[10px] text-oak-500">{preset.description}</div>
                </button>
              ))}
            </div>
          </div>
        ) : null}
        <button
          onClick={() => { deleteSelected(); hideContextMenu(); }}
          className="flex w-full items-center gap-2 border-t border-birch-200 px-3 py-2 text-left text-danger hover:bg-danger-soft"
        >
          <Trash2 className="h-4 w-4" />
          <span>{selCount > 1 ? `删除选中 (${selCount})` : '删除层'}</span>
        </button>
        <button
          onClick={hideContextMenu}
          className="flex w-full items-center gap-2 rounded-b-xl border-t border-birch-200 px-3 py-2 text-left hover:bg-pine-50"
        >
          <X className="h-4 w-4" />
          <span>取消</span>
        </button>
      </div>
    </div>
  );
}

